import { useEffect, useRef, useState } from "react";
import type React from "react";
import { Spinner } from "./Spinner";

const startDeg = 140;
const sweepDeg = 260;

/**
 * Temperature dial (spec §3.2): 292×292 arc starting at 140° sweeping 260°
 * clockwise, 16px round-capped stroke, 31px knob at the fill end. Dragging
 * updates a local draft; the value is committed only on pointer release.
 */
export function TemperatureDial({
  label,
  value,
  min,
  max,
  tint = "var(--cool)",
  size = 292,
  disabled = false,
  pending = false,
  onCommit
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  /** CSS color for the fill arc + knob. */
  tint?: string;
  size?: number;
  disabled?: boolean;
  pending?: boolean;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [dragging, setDragging] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);

  // Live-sync from server state unless mid-drag.
  useEffect(() => {
    if (!dragging) setDraft(value);
  }, [dragging, value]);

  const shown = clamp(dragging ? draft : value, min, max);
  const progress = clamp((shown - min) / (max - min), 0, 1);

  const lineWidth = clamp(size * 0.055, 14, 18);
  const center = size / 2;
  const radius = center - (lineWidth / 2 + 5);
  const knobDiameter = clamp(lineWidth + 15, 28, 34);
  const fillEndDeg = startDeg + sweepDeg * progress;
  const knob = polar(center, center, radius, fillEndDeg);

  function valueFromPointer(event: React.PointerEvent): number {
    const el = surfaceRef.current;
    if (!el) return shown;
    const rect = el.getBoundingClientRect();
    const dx = event.clientX - (rect.left + rect.width / 2);
    const dy = event.clientY - (rect.top + rect.height / 2);
    let deg = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (deg < startDeg) deg += 360;
    deg = clamp(deg, startDeg, startDeg + sweepDeg);
    return clamp(Math.round(min + ((deg - startDeg) / sweepDeg) * (max - min)), min, max);
  }

  function commit(next: number) {
    const clamped = clamp(Math.round(next), min, max);
    setDraft(clamped);
    if (clamped !== value) onCommit(clamped);
  }

  return (
    <div className={`dial-block${disabled ? " disabled" : ""}`} style={{ width: size, height: size }}>
      {pending ? (
        <span className="dial-spinner">
          <Spinner size={20} />
        </span>
      ) : null}
      <div
        ref={surfaceRef}
        className="dial-surface"
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={shown}
        onPointerDown={(event) => {
          if (disabled) return;
          setDragging(true);
          event.currentTarget.setPointerCapture(event.pointerId);
          setDraft(valueFromPointer(event));
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) setDraft(valueFromPointer(event));
        }}
        onPointerUp={(event) => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
          event.currentTarget.releasePointerCapture(event.pointerId);
          setDragging(false);
          commit(valueFromPointer(event));
        }}
        onPointerCancel={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
          setDragging(false);
          setDraft(value);
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === "ArrowUp" || event.key === "ArrowRight") {
            event.preventDefault();
            commit(value + 1);
          }
          if (event.key === "ArrowDown" || event.key === "ArrowLeft") {
            event.preventDefault();
            commit(value - 1);
          }
        }}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <path
            d={arcPath(center, center, radius, startDeg, startDeg + sweepDeg)}
            fill="none"
            stroke="var(--dial-track)"
            strokeWidth={lineWidth}
            strokeLinecap="round"
          />
          {progress > 0 ? (
            <path
              d={arcPath(center, center, radius, startDeg, fillEndDeg)}
              fill="none"
              stroke={tint}
              strokeWidth={lineWidth}
              strokeLinecap="round"
            />
          ) : null}
          <circle
            cx={knob.x}
            cy={knob.y}
            r={knobDiameter / 2}
            fill={tint}
            stroke="#ffffff"
            strokeWidth={4}
            style={{ filter: "drop-shadow(0 3px 6px rgba(0, 0, 0, 0.10))" }}
          />
        </svg>
        <span className="dial-readout" style={{ fontSize: size * 0.225 }}>
          <strong>{shown}°</strong>
          <span style={{ fontSize: size * 0.064 }}>SET</span>
        </span>
      </div>
    </div>
  );
}

function polar(cx: number, cy: number, r: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx: number, cy: number, r: number, fromDeg: number, toDeg: number): string {
  const start = polar(cx, cy, r, fromDeg);
  const end = polar(cx, cy, r, toDeg);
  const largeArc = toDeg - fromDeg > 180 ? 1 : 0;
  return `M ${start.x.toFixed(3)} ${start.y.toFixed(3)} A ${r} ${r} 0 ${largeArc} 1 ${end.x.toFixed(3)} ${end.y.toFixed(3)}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
